import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import AdminService from "../../../api/services/adminService";
import "../Home/Homepage.css";
import "../Trading/AllOpenOrdersPage.css";
import "./FundsWithdrawalPage.css";

const LIMIT = 50;

const VARIANT = {
  deposits: {
    title: "On-chain deposits",
    subtitle: "User deposits credited on-chain (DEPOSIT)",
    sectionTitle: "Deposits",
    detailTitle: "Deposit Details",
    breadcrumb: "Funds",
    icon: "fa-wallet",
    fetch: (p) => AdminService.getGlobalWalletDeposits(p),
    depositExtras: true,
    showTransactionType: false,
    showDescription: false,
    limit: 50,
  },
  gas: {
    title: "Gas fee transfers",
    subtitle: "Gas top-ups for token transfers",
    sectionTitle: "Gas Fee Transfers",
    detailTitle: "Gas Fee Transfer Details",
    breadcrumb: "Wallet",
    icon: "fa-bolt",
    fetch: (p) => AdminService.getGlobalGasFeeTransfers(p),
    depositExtras: false,
    showTransactionType: true,
    showDescription: true,
    limit: 20,
  },
  hot: {
    title: "Hot wallet transfers",
    subtitle: "Member → admin / hot wallet token moves",
    sectionTitle: "Hot Wallet Transfers",
    detailTitle: "Hot Wallet Transfer Details",
    breadcrumb: "Wallet",
    icon: "fa-fire",
    fetch: (p) => AdminService.getGlobalHotWalletTransfers(p),
    depositExtras: false,
    showTransactionType: true,
    showDescription: true,
    limit: 20,
  },
};

const truncateHash = (str, head = 8, tail = 6) => {
  if (!str || str.length <= head + tail) return str || "—";
  return `${str.slice(0, head)}...${str.slice(-tail)}`;
};

const statusClass = (status) => {
  const s = String(status || "").toLowerCase();
  if (s === "rejected" || s === "cancelled" || s === "failed") return "rejected";
  if (s === "completed" || s === "complete" || s === "success") return "completed";
  if (s === "pending") return "pending";
  return s;
};

const CopyHashCell = ({ value, head = 6, tail = 4, onCopy }) => {
  if (!value) return <span className="withdrawal-hash">—</span>;
  return (
    <div className="withdrawal-address-cell">
      <div className="withdrawal-tooltip-wrap">
        <span className="withdrawal-hash withdrawal-tooltip-wrap__trigger">
          {truncateHash(value, head, tail)}
        </span>
        <div className="withdrawal-tooltip-box" role="tooltip">
          {value}
        </div>
      </div>
      <button
        type="button"
        className="withdrawal-copy-btn"
        onClick={() => onCopy(value)}
        title="Copy"
        aria-label="Copy"
      >
        <i className="fas fa-copy" />
      </button>
    </div>
  );
};

const GlobalWalletTransactionsPage = ({ variant = "deposits" }) => {
  const cfg = useMemo(() => VARIANT[variant] || VARIANT.deposits, [variant]);
  const depositExtras = cfg.depositExtras;
  const pageLimit = cfg.limit ?? LIMIT;
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalCount, setTotalCount] = useState(null);

  const [userSearch, setUserSearch] = useState("");
  const [debouncedUser, setDebouncedUser] = useState("");
  const [currencyId, setCurrencyId] = useState("");
  const [chain, setChain] = useState("");
  const [shortName, setShortName] = useState("");
  const [status, setStatus] = useState("");
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");
  const [transactionHash, setTransactionHash] = useState("");
  const [transferStatus, setTransferStatus] = useState("");
  const [transferTxHash, setTransferTxHash] = useState("");
  const [viewDetail, setViewDetail] = useState(null);
  const [toast, setToast] = useState(null);

  useEffect(() => {
    const t = setTimeout(() => setDebouncedUser(userSearch.trim()), 1000);
    return () => clearTimeout(t);
  }, [userSearch]);

  useEffect(() => {
    setPage(1);
  }, [
    debouncedUser,
    currencyId,
    chain,
    shortName,
    status,
    from,
    to,
    transactionHash,
    transferStatus,
    transferTxHash,
  ]);

  const buildParams = useCallback(() => {
    const params = { page, limit: pageLimit };
    if (debouncedUser) params.user = debouncedUser;
    if (currencyId.trim()) params.currency_id = currencyId.trim();
    if (chain.trim()) params.chain = chain.trim();
    if (shortName.trim()) params.short_name = shortName.trim();
    if (status.trim()) params.status = status.trim();
    if (from) {
      const d = new Date(from);
      if (!Number.isNaN(d.getTime())) params.from = d.toISOString();
    }
    if (to) {
      const d = new Date(to);
      if (!Number.isNaN(d.getTime())) params.to = d.toISOString();
    }
    if (transactionHash.trim()) params.transaction_hash = transactionHash.trim();
    if (depositExtras) {
      if (transferStatus.trim()) params.transfer_status = transferStatus.trim();
      if (transferTxHash.trim()) params.transfer_tx_hash = transferTxHash.trim();
    }
    return params;
  }, [
    page,
    debouncedUser,
    currencyId,
    chain,
    shortName,
    status,
    from,
    to,
    transactionHash,
    transferStatus,
    transferTxHash,
    depositExtras,
    pageLimit,
  ]);

  useEffect(() => {
    const run = async () => {
      setLoading(true);
      setError("");
      try {
        const res = await (VARIANT[variant] || VARIANT.deposits).fetch(buildParams());
        const list = Array.isArray(res?.data) ? res.data : [];
        setRows(list);
        setTotalPages(res?.pagination?.total_pages || 1);
        setTotalCount(
          typeof res?.pagination?.total === "number" ? res.pagination.total : null
        );
        if (res?.success === false && !list.length) {
          setError(res?.message || "Failed to load data.");
        }
        if (variant === "deposits") {
          window.dispatchEvent(new Event("dashboardCountsUpdated"));
        }
      } catch (e) {
        console.error(e);
        setRows([]);
        setError("Failed to load data.");
      } finally {
        setLoading(false);
      }
    };
    run();
  }, [buildParams, variant]);

  const formatDate = (iso) => {
    if (!iso) return "—";
    try {
      return new Date(iso).toLocaleString("en-IN", {
        day: "2-digit",
        month: "2-digit",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
      });
    } catch {
      return iso;
    }
  };

  const formatNum = (n) =>
    n != null && !Number.isNaN(Number(n))
      ? Number(n).toLocaleString("en-IN", { maximumFractionDigits: 12 })
      : "—";

  const userFullName = (u) => {
    if (!u) return "—";
    const first = u.first_name ?? u.firstName;
    const last = u.last_name ?? u.lastName;
    const parts = [first, last].filter(Boolean);
    return parts.length ? parts.join(" ") : "—";
  };

  const handleCopy = async (text) => {
    if (!text) return;
    try {
      await navigator.clipboard.writeText(String(text));
      setToast({ type: "success", message: "Copied to clipboard." });
      setTimeout(() => setToast(null), 2000);
    } catch {
      setToast({ type: "error", message: "Failed to copy." });
      setTimeout(() => setToast(null), 2000);
    }
  };

  const getRowTxHash = (row) => row.transaction_hash ?? row.tx_hash;
  const getRowTransferTx = (row) => row.transfer_tx_hash ?? row.transferTxHash;
  const getRowTransferStatus = (row) => row.transfer_status ?? row.transferStatus;
  const getRowDescription = (row) => row.description ?? row.memo;

  return (
    <main className="dashboard-main dashboard-subpage">
      <section className="dashboard-hero">
        <div className="dashboard-hero__text">
          <p className="dashboard-hero__breadcrumb">
            <Link to="/dashboard/homepage">Dashboard</Link>
            <span style={{ margin: "0 0.35rem" }}>/</span>
            <span>{cfg.breadcrumb}</span>
            <span style={{ margin: "0 0.35rem" }}>/</span>
            <span>{cfg.title}</span>
          </p>
          <h1 className="dashboard-hero__title">{cfg.title}</h1>
          <p className="dashboard-hero__subtitle">{cfg.subtitle}</p>
        </div>
        <div className="dashboard-hero__icon">
          <i className={`fas ${cfg.icon}`} />
        </div>
      </section>

      {toast && (
        <div
          className={`withdrawal-toast withdrawal-toast--${toast.type}`}
          role="status"
        >
          <i className={`fas fa-${toast.type === "success" ? "check-circle" : "exclamation-circle"}`} />
          {toast.message}
        </div>
      )}

      <div className="dashboard-body">
        <section className="openorders-section">
          <div className="openorders-section__header">
            <div className="openorders-section__title-wrap">
              <span className="openorders-section__icon">
                <i className="fas fa-list" />
              </span>
              <h2 className="openorders-section__title">{cfg.sectionTitle}</h2>
            </div>
            <div className="openorders-section__stats">
              <span className="openorders-stat">
                <strong>{rows.length}</strong> on page
                {totalCount != null && (
                  <>
                    {" "}
                    (<strong>{totalCount}</strong> total)
                  </>
                )}
              </span>
              {totalPages > 1 && (
                <span className="openorders-stat">
                  Page <strong>{page}</strong> / <strong>{totalPages}</strong>
                </span>
              )}
            </div>
          </div>

          {error && (
            <p style={{ color: "#dc2626", padding: "0 0.5rem 0.75rem", margin: 0 }}>{error}</p>
          )}

          <div
            className="openorders-search-wrap"
            style={{ flexWrap: "wrap", gap: "0.5rem", alignItems: "flex-end" }}
          >
            <input
              type="text"
              placeholder="User (email / UUID / phone) — 1s debounce"
              value={userSearch}
              onChange={(e) => setUserSearch(e.target.value)}
              className="openorders-search-input"
              style={{ minWidth: 200, flex: "1 1 220px" }}
            />
            <input
              type="text"
              placeholder="Currency ObjectId"
              value={currencyId}
              onChange={(e) => setCurrencyId(e.target.value)}
              className="openorders-search-input"
              style={{ minWidth: 120, flex: "0 1 180px" }}
            />
            <input
              type="text"
              placeholder="Chain"
              value={chain}
              onChange={(e) => setChain(e.target.value)}
              className="openorders-search-input"
              style={{ minWidth: 80, flex: "0 1 120px" }}
            />
            <input
              type="text"
              placeholder="Short name"
              value={shortName}
              onChange={(e) => setShortName(e.target.value)}
              className="openorders-search-input"
              style={{ minWidth: 100, flex: "0 1 140px" }}
            />
            <input
              type="text"
              placeholder="Status (exact, comma, or all)"
              value={status}
              onChange={(e) => setStatus(e.target.value)}
              className="openorders-search-input"
              style={{ minWidth: 140, flex: "0 1 200px" }}
            />
            <input
              type="text"
              placeholder="Tx hash (partial)"
              value={transactionHash}
              onChange={(e) => setTransactionHash(e.target.value)}
              className="openorders-search-input"
              style={{ minWidth: 140, flex: "0 1 200px" }}
            />
            {depositExtras && (
              <>
                <input
                  type="text"
                  placeholder="Transfer status"
                  value={transferStatus}
                  onChange={(e) => setTransferStatus(e.target.value)}
                  className="openorders-search-input"
                  style={{ minWidth: 120, flex: "0 1 160px" }}
                />
                <input
                  type="text"
                  placeholder="Transfer tx hash (partial)"
                  value={transferTxHash}
                  onChange={(e) => setTransferTxHash(e.target.value)}
                  className="openorders-search-input"
                  style={{ minWidth: 140, flex: "0 1 200px" }}
                />
              </>
            )}
            <label style={{ display: "flex", flexDirection: "column", fontSize: 12, gap: 2 }}>
              From
              <input
                type="datetime-local"
                value={from}
                onChange={(e) => setFrom(e.target.value)}
                className="openorders-search-input"
                style={{ minWidth: 180, margin: 0 }}
              />
            </label>
            <label style={{ display: "flex", flexDirection: "column", fontSize: 12, gap: 2 }}>
              To
              <input
                type="datetime-local"
                value={to}
                onChange={(e) => setTo(e.target.value)}
                className="openorders-search-input"
                style={{ minWidth: 180, margin: 0 }}
              />
            </label>
          </div>

          {loading ? (
            <div className="openorders-empty">
              <i className="fas fa-spinner fa-spin" /> Loading…
            </div>
          ) : rows.length > 0 ? (
            <>
              <div className="openorders-table-wrap">
                <table className="openorders-table">
                  <thead>
                    <tr>
                      <th>UUID</th>
                      <th>Name</th>
                      <th>Email</th>
                      <th>Phone</th>
                      <th>Chain</th>
                      <th>Currency</th>
                      {cfg.showTransactionType && <th>Type</th>}
                      <th>Amount</th>
                      <th>Fee</th>
                      <th>From</th>
                      <th>To</th>
                      <th>Tx hash</th>
                      <th>Status</th>
                      <th>Date</th>
                      {cfg.showDescription && <th>Description</th>}
                      <th>View</th>
                    </tr>
                  </thead>
                  <tbody>
                    {rows.map((row, idx) => {
                      const rid = row._id || row.id || `row-${idx}`;
                      const uid = row.user?.id || row.user_id;
                      const u = row.user;
                      const uuidStr = u?.uuid ?? "—";
                      const txh = getRowTxHash(row);
                      const desc = getRowDescription(row);
                      return (
                        <tr key={rid}>
                          <td>
                            {uid ? (
                              <Link
                                to={`/dashboard/trader/${uid}`}
                                state={{ trader: row.user }}
                                style={{
                                  color: "#dc2626",
                                  fontWeight: 600,
                                  textDecoration: "none",
                                }}
                              >
                                {uuidStr}
                              </Link>
                            ) : (
                              uuidStr
                            )}
                          </td>
                          <td>{userFullName(u)}</td>
                          <td>{u?.email ?? "—"}</td>
                          <td>{u?.phone ?? "—"}</td>
                          <td>{row.chain ?? "—"}</td>
                          <td>
                            <strong>{row.short_name ?? row.currency ?? "—"}</strong>
                          </td>
                          {cfg.showTransactionType && (
                            <td>{row.transaction_type ?? "—"}</td>
                          )}
                          <td>{formatNum(row.amount)}</td>
                          <td>{formatNum(row.fee)}</td>
                          <td>
                            <CopyHashCell
                              value={row.from_address}
                              onCopy={handleCopy}
                            />
                          </td>
                          <td>
                            <CopyHashCell
                              value={row.to_address}
                              onCopy={handleCopy}
                            />
                          </td>
                          <td>
                            <CopyHashCell
                              value={txh}
                              head={10}
                              tail={8}
                              onCopy={handleCopy}
                            />
                          </td>
                          <td>
                            <span
                              className={`withdrawal-status withdrawal-status--${statusClass(row.status)}`}
                            >
                              {row.status ?? "—"}
                            </span>
                          </td>
                          <td>{formatDate(row.createdAt)}</td>
                          {cfg.showDescription && (
                            <td style={{ maxWidth: 220, fontSize: "0.85rem" }}>
                              {desc ? String(desc).slice(0, 120) : "—"}
                            </td>
                          )}
                          <td>
                            <button
                              type="button"
                              className="withdrawal-btn withdrawal-btn--view"
                              onClick={() => setViewDetail(row)}
                              title="View request details"
                            >
                              <i className="fas fa-eye" /> View
                            </button>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
              {totalPages > 1 && (
                <div style={{ padding: "0.75rem 0", display: "flex", gap: "0.5rem" }}>
                  <button
                    type="button"
                    className="cp-btn cp-btn--outline"
                    style={{ margin: 0 }}
                    disabled={page <= 1 || loading}
                    onClick={() => setPage((p) => Math.max(1, p - 1))}
                  >
                    Previous
                  </button>
                  <button
                    type="button"
                    className="cp-btn cp-btn--outline"
                    style={{ margin: 0 }}
                    disabled={page >= totalPages || loading}
                    onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                  >
                    Next
                  </button>
                </div>
              )}
            </>
          ) : (
            <p className="openorders-empty">No transactions match your filters.</p>
          )}
        </section>
      </div>

      {viewDetail && (
        <div className="withdrawal-modal-overlay" onClick={() => setViewDetail(null)}>
          <div className="withdrawal-modal" onClick={(e) => e.stopPropagation()}>
            <div className="withdrawal-modal__header">
              <h3>{cfg.detailTitle}</h3>
              <button
                type="button"
                className="withdrawal-modal__close"
                onClick={() => setViewDetail(null)}
                aria-label="Close"
              >
                <i className="fas fa-times" />
              </button>
            </div>
            <div className="withdrawal-modal__body">
              <dl className="withdrawal-dl">
                <div>
                  <dt>Transaction ID</dt>
                  <dd className="withdrawal-mono withdrawal-mono--wrap">
                    {viewDetail._id || viewDetail.id || "—"}
                  </dd>
                </div>
                <div>
                  <dt>Date</dt>
                  <dd>{formatDate(viewDetail.createdAt)}</dd>
                </div>
                <div>
                  <dt>UUID</dt>
                  <dd className="withdrawal-mono withdrawal-mono--wrap">
                    {viewDetail.user?.uuid ?? "—"}
                  </dd>
                </div>
                <div>
                  <dt>Name</dt>
                  <dd>{userFullName(viewDetail.user)}</dd>
                </div>
                <div>
                  <dt>Email</dt>
                  <dd>{viewDetail.user?.email ?? "—"}</dd>
                </div>
                <div>
                  <dt>Phone</dt>
                  <dd>{viewDetail.user?.phone ?? "—"}</dd>
                </div>
                <div>
                  <dt>Chain</dt>
                  <dd>{viewDetail.chain ?? "—"}</dd>
                </div>
                <div>
                  <dt>Currency</dt>
                  <dd>{viewDetail.short_name ?? viewDetail.currency ?? "—"}</dd>
                </div>
                {viewDetail.transaction_type && (
                  <div>
                    <dt>Transaction Type</dt>
                    <dd>{viewDetail.transaction_type}</dd>
                  </div>
                )}
                {viewDetail.order_id && (
                  <div>
                    <dt>Order ID</dt>
                    <dd className="withdrawal-mono withdrawal-mono--wrap">{viewDetail.order_id}</dd>
                  </div>
                )}
                {viewDetail.currency_id && (
                  <div>
                    <dt>Currency ID</dt>
                    <dd className="withdrawal-mono withdrawal-mono--wrap">{viewDetail.currency_id}</dd>
                  </div>
                )}
                <div>
                  <dt>Amount</dt>
                  <dd>{formatNum(viewDetail.amount)}</dd>
                </div>
                <div>
                  <dt>Fee</dt>
                  <dd>{formatNum(viewDetail.fee)}</dd>
                </div>
                <div>
                  <dt>From</dt>
                  <dd className="withdrawal-mono withdrawal-mono--wrap">
                    {viewDetail.from_address || "—"}
                  </dd>
                </div>
                <div>
                  <dt>To</dt>
                  <dd className="withdrawal-mono withdrawal-mono--wrap">
                    {viewDetail.to_address || "—"}
                  </dd>
                </div>
                <div>
                  <dt>Tx Hash</dt>
                  <dd className="withdrawal-mono withdrawal-mono--wrap">
                    {getRowTxHash(viewDetail) || "—"}
                  </dd>
                </div>
                {depositExtras && (
                  <>
                    <div>
                      <dt>Transfer Status</dt>
                      <dd>{getRowTransferStatus(viewDetail) ?? "—"}</dd>
                    </div>
                    <div>
                      <dt>Transfer Tx</dt>
                      <dd className="withdrawal-mono withdrawal-mono--wrap">
                        {getRowTransferTx(viewDetail) || "—"}
                      </dd>
                    </div>
                  </>
                )}
                {!depositExtras && getRowDescription(viewDetail) && (
                  <div>
                    <dt>Description</dt>
                    <dd>{getRowDescription(viewDetail)}</dd>
                  </div>
                )}
                {viewDetail.wallet_type && (
                  <div>
                    <dt>Wallet Type</dt>
                    <dd>{viewDetail.wallet_type}</dd>
                  </div>
                )}
                {viewDetail.retry_count != null && (
                  <div>
                    <dt>Retry Count</dt>
                    <dd>{viewDetail.retry_count}</dd>
                  </div>
                )}
                {viewDetail.updatedAt && (
                  <div>
                    <dt>Updated</dt>
                    <dd>{formatDate(viewDetail.updatedAt)}</dd>
                  </div>
                )}
                <div>
                  <dt>Status</dt>
                  <dd>
                    <span
                      className={`withdrawal-status withdrawal-status--${statusClass(viewDetail.status)}`}
                    >
                      {viewDetail.status ?? "—"}
                    </span>
                  </dd>
                </div>
              </dl>
            </div>
          </div>
        </div>
      )}
    </main>
  );
};

export default GlobalWalletTransactionsPage;
